import { useEffect, useRef, useState } from 'react'
import usePageVisibility from '../utils/usePageVisibility'

/*
 * Polls the deployed version.json and reports whether the server is now serving a
 * different build than the one this tab is running.
 *
 * REACT_APP_BUILD_ID is inlined at build time by scripts/writeVersion.js, which writes
 * the same id into public/version.json. No id means we are on the dev server, where
 * there is nothing to compare and nothing to reload for — the hook stays inert.
 */

const BUILD_ID = process.env.REACT_APP_BUILD_ID
const POLL_INTERVAL_MS = 5 * 60 * 1000

export default function useVersionCheck(intervalMs = POLL_INTERVAL_MS) {
    const [updateAvailable, setUpdateAvailable] = useState(false)
    // usePageVisibility reports the raw document.hidden value: true means hidden.
    const pageIsNotVisible = usePageVisibility()
    const checkRef = useRef(null)

    useEffect(() => {
        if (!BUILD_ID || updateAvailable) return undefined

        let cancelled = false

        const check = async () => {
            try {
                // Bust both the HTTP cache and any intermediary that ignores it.
                const response = await fetch(`/version.json?t=${Date.now()}`, {
                    cache: 'no-store',
                    credentials: 'omit',
                })
                if (!response.ok) return

                const data = await response.json()
                if (cancelled || !data?.buildId) return

                if (data.buildId !== BUILD_ID) setUpdateAvailable(true)
            } catch {
                // Offline, a flaky network, or a half-written file mid-deploy. Staying
                // on the current build is always the safe answer, so do nothing.
            }
        }

        checkRef.current = check
        check()

        const timer = setInterval(() => {
            // No point polling a tab nobody is looking at; the visibility effect below
            // catches up the moment it comes back.
            if (document.hidden) return
            check()
        }, intervalMs)

        return () => {
            cancelled = true
            clearInterval(timer)
        }
    }, [intervalMs, updateAvailable])

    // Returning to a tab left open overnight is the most likely moment for the build to
    // have moved on, so check immediately rather than waiting out the interval.
    useEffect(() => {
        if (!BUILD_ID || updateAvailable || pageIsNotVisible) return
        checkRef.current?.()
    }, [pageIsNotVisible, updateAvailable])

    return updateAvailable
}
